import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * Drag handle component - only this triggers drag
 */
export const DragHandle = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'touch-none cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors',
        className
      )}
      {...props}
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
});
DragHandle.displayName = 'DragHandle';

/**
 * Mobile fallback buttons for moving items up/down
 */
export const MoveButtons = ({ onMoveUp, onMoveDown, canMoveUp, canMoveDown, className }) => {
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0"
        onClick={onMoveUp}
        disabled={!canMoveUp}
      >
        <ChevronUp className="w-3 h-3" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0"
        onClick={onMoveDown}
        disabled={!canMoveDown}
      >
        <ChevronDown className="w-3 h-3" />
      </Button>
    </div>
  );
};

/**
 * Sortable item wrapper - wraps any component to make it sortable
 */
export const SortableItem = ({ 
  id, 
  children, 
  disabled = false,
  className,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={cn(
        isDragging && 'shadow-lg ring-2 ring-blue-500 ring-opacity-50',
        className
      )}
    >
      {typeof children === 'function' 
        ? children({ attributes, listeners, isDragging })
        : children
      }
    </div>
  );
};

/**
 * Sortable container - provides DnD context for children
 */
export const SortableContainer = ({
  items,
  onDragEnd,
  children,
  strategy = 'vertical', // 'vertical' or 'grid'
  disabled = false,
}) => {
  // Configure sensors for desktop and touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms hold to start drag on touch
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      onDragEnd(active.id, over.id);
    }
  };

  const sortingStrategy = strategy === 'grid' 
    ? rectSortingStrategy 
    : verticalListSortingStrategy;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext 
        items={items.map(item => item.id || item)} 
        strategy={sortingStrategy}
        disabled={disabled}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};

export default SortableContainer;
